const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const QUALITY_MESSAGES = {
  configuration_pending_materialization: "A configuração financeira mudou e a leitura ainda está sendo atualizada.",
  critical_stale: "A atualização das fontes está muito atrasada. Confira a origem e a data antes de usar esta leitura.",
  stale: "Há fontes com atualização atrasada. Confira a data antes de usar esta leitura.",
  plan_missing: "O planejamento financeiro não cobre toda a janela selecionada.",
  source_incomplete: "As fontes financeiras não cobrem todas as competências do período selecionado.",
  reconciliation_pending: "Há movimentos aguardando conferência entre as fontes financeiras.",
  project_month_reconciliation: "A conciliação está no nível de projeto e mês; o detalhamento por centro de custo ainda não é definitivo.",
  monetary_basis_incomplete: "A base corrigida ainda não está completa para contratado, recebido e previsto.",
  key_handover_unavailable: "O valor de recebimento nas chaves ainda não possui fonte ou regra aprovada.",
  origin_unit_invariant_violation: "Há recebimento associado a uma unidade fora da regra de origem aprovada.",
  source_restricted: "A fonte de recebimentos está sem permissão de leitura para esta empresa.",
  opening_balance_missing: "Não há saldo inicial declarado. A leitura mostra fluxo, não saldo de caixa.",
  ledger_partial_coverage: "Uma parcela pequena da fonte ficou em quarentena e não foi convertida em zero.",
  date_outlier: "Há título com data fora do intervalo financeiro suportado.",
  planned_unavailable: "Uma obra usa Planejamento empresarial sem composição do planejado; o planejado dela conta como zero.",
  partial_pool_scope: "Algum caixa tem obras fora do escopo contratado da Diretoria.",
  opening_balance_pending_materialization: "Um saldo inicial novo foi informado e ainda está sendo processado.",
  native_cost_plan_composition_unavailable: "Uma obra do caixa usa Planejamento empresarial e o saldo ainda não pode ser composto.",
};

// Motivos que impedem a Diretoria de mostrar saldo projetado (SPEC 5.4, aceite 4).
const BALANCE_BLOCKER_MESSAGES = {
  opening_balance_missing: "Falta o saldo inicial de algum caixa.",
  native_cost_plan_composition_unavailable: "Uma obra do caixa usa Planejamento empresarial e o saldo ainda não pode ser composto.",
  partial_pool_scope: "Algum caixa tem obras fora do escopo contratado da Diretoria, então o saldo dele não fecha.",
  critical_stale: "As fontes estão muito desatualizadas para projetar saldo com segurança.",
  configuration_pending_materialization: "A configuração financeira mudou e o saldo ainda está sendo recalculado.",
  opening_balance_pending_materialization: "Um saldo inicial novo foi informado e ainda está sendo processado.",
};

export const balanceBlockerMessage = (code) => (
  BALANCE_BLOCKER_MESSAGES[code] || "O saldo projetado está bloqueado por uma limitação da fonte."
);

const COST_CENTER_ROLE_LABELS = {
  construction: "Obra",
  administrative: "Administrativo",
  corporate: "Corporativo",
  sales: "Comercial",
  revenue: "Receita",
  permutation: "Permuta",
};

const DRIVER_CAUSE_LABELS = {
  cost_overrun: "pressão de custos da obra",
  payment_timing: "concentração de pagamentos no período",
  other: "outra pressão identificada",
};

export const formatCents = (value) => (
  Number.isSafeInteger(value) ? currencyFormatter.format(value / 100) : "Indisponível"
);

// Presentation aggregation only. Missing source values must not become a known zero.
export const sumKnownCents = (...values) => {
  if (!values.length || values.some(value => !Number.isSafeInteger(value))) return null;
  const total = values.reduce((sum, value) => sum + BigInt(value), 0n);
  return total > BigInt(Number.MAX_SAFE_INTEGER) || total < BigInt(Number.MIN_SAFE_INTEGER) ? null : Number(total);
};

// Valor executivo curto ("R$ 50,5 mi", "-R$ 5,9 mi"). Só apresentação: nunca usar em cálculo.
export const compactCents = (value) => {
  if (!Number.isSafeInteger(value)) return "Indisponível";
  const amount = Math.abs(value) / 100;
  const sign = value < 0 ? "-" : "";
  const formatter = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  if (amount >= 1_000_000) return `${sign}R$ ${formatter.format(amount / 1_000_000)} mi`;
  if (amount >= 1_000) return `${sign}R$ ${formatter.format(amount / 1_000)} mil`;
  return `${sign}R$ ${formatter.format(amount)}`;
};

const SHORT_MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// Competência curta para eixos e frases ("ago/27").
export const formatCompetenceShort = (value) => {
  if (typeof value !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return "Competência inválida";
  }
  const [year, month] = value.split("-");
  return `${SHORT_MONTHS[Number(month) - 1]}/${year.slice(2)}`;
};

export const formatBps = (value) => (
  Number.isSafeInteger(value)
    ? new Intl.NumberFormat("pt-BR", {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value / 10000)
    : "Indisponível"
);

export const formatBasis = (value) => ({
  nominal: "Nominal",
  corrected: "Corrigida",
}[value] || "Base não informada");

export const formatCompetence = (value) => {
  if (typeof value !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return "Competência inválida";
  }
  const [year, month] = value.split("-").map(Number);
  return monthFormatter.format(new Date(Date.UTC(year, month - 1, 1)));
};

export const qualityMessage = (code) => (
  QUALITY_MESSAGES[code]
  || "Existe uma limitação de qualidade informada pela fonte. Revise a atualização e o recorte exibido."
);

export const formatCostCenterRole = (role) => (
  typeof role === "string" && role
    ? COST_CENTER_ROLE_LABELS[role] || role
    : "Não classificado"
);

export const formatDriverCause = (cause) => (
  typeof cause === "string" && cause
    ? DRIVER_CAUSE_LABELS[cause] || cause.replaceAll("_", " ")
    : "Causa não classificada"
);

export const formatFreshness = (value) => {
  if (typeof value !== "string") return "Atualização indisponível";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Atualização indisponível";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(date);
};

export const formatCivilDate = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Data indisponível";
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "Data indisponível";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(date);
};

export default {
  formatCents,
  compactCents,
  formatCompetenceShort,
  balanceBlockerMessage,
  formatBps,
  formatBasis,
  formatCompetence,
  formatCostCenterRole,
  formatDriverCause,
  qualityMessage,
  formatFreshness,
  formatCivilDate,
};
